import React from 'react';

export interface NavigationItem {
  title: string;
  path: string;
  icon: React.ReactNode;
  icon_size: number;
  children?: NavigationItem[];
  permission?: string; // The permission key to check for this menu item
}

// Mapping of menu titles to their corresponding permission keys
export const MENU_PERMISSIONS: Record<string, string> = {
  // Main menus
  'Dashboard': 'dashboard_menu', // Dashboard should always be visible
  'User Management': 'user_mgmt_menu',
  'Assets Listing': 'view_asset_scr',
  'Asset Transfer': 'asset_transfer_menu',
  'Asset Disposal': 'asset_disposal_menu',
  'Floor Plan Management': 'floor_plan_menu',
  'Depreciation': 'depreciation_menu',
  'Reports': 'reports_menu',
  'Audit Management': 'audit_mgmt_menu',
  'RFID Management': 'rfid_mgmt_menu',
  'Schedule Management': 'schedule_mgmt_menu',
  'Master Data': 'master_menu',
  'Workflow Management': 'workflow_mgmt_menu',
  'Bulk Upload': 'bulk_upload_menu',
  'Profile': 'user_profile_menu',

  // User Management sub-menus
  'User List': 'view_user_scr',
  'Invite User': 'add_user_scr',
  'Role Management': 'view_role_scr',

  // Floor Plan Management sub-menus
  'Floor Plans': 'manage_floor_plan_scr',
  'Interactive Editor': 'manage_floor_plan_scr',

  // Audit Management sub-menus
  'Audit Requests': 'view_audit_scr',
  'Pending Audits': 'view_audit_task_scr',
  'Reverifier Audits': 'view_audit_task_scr',
  
  // RFID Management sub-menus
  'RFID Readers': 'view_reader_scr',
  'Vehicle Management': 'view_vehicle_scr',

  // Schedule Management sub-menus
  'Schedules': 'view_schedule_scr',
  'Activities': 'view_activity_scr',
  'Create One-Off Activity': 'add_activity_scr',
  'User Groups': 'view_user_group_scr',
  'Activity Types': 'view_activity_type_scr',
  'Checklist Templates': 'view_checklist_template_scr',
  'Cost Templates': 'view_cost_template_scr',

  // Workflow Management sub-menus
  'Workflow Templates': 'view_workflow_template_scr',
  'Approval Tasks': 'view_workflow_scr',
  'Workflow Transactions': 'view_workflow_scr',

  // Reports sub-menus
  'All Reports': 'view_report_scr',

  //Custom Forms Management
  'Custom Forms Management': 'custom_form_menu',
};

// Path-based permission mapping for duplicate menu titles
const PATH_PERMISSIONS: Record<string, string> = {
  // Master Data paths
  '/asset-categories': 'view_asset_category_scr',
  '/asset-statuses': 'view_asset_status_scr',
  '/asset-conditions': 'view_asset_condition_scr',
  '/sites': 'view_site_scr',
  '/locations': 'view_location_scr',
  '/departments': 'view_department_scr',
  '/suppliers': 'view_supplier_scr',
  '/manufacturers': 'view_manufacturer_scr',
  '/models': 'view_model_scr',

  // Bulk Upload paths
  '/bulk-upload/assets': 'view_bulk_upload_scr',
  '/bulk-upload/asset_images': 'view_bulk_upload_scr',
  '/bulk-upload/locations': 'view_bulk_upload_scr',
  '/bulk-upload/departments': 'view_bulk_upload_scr',
  '/bulk-upload/categories': 'view_bulk_upload_scr',
  '/bulk-upload/sites': 'view_bulk_upload_scr',
  '/bulk-upload/users': 'view_bulk_upload_scr',
  '/bulk-upload/supplier': 'view_bulk_upload_scr',
  '/bulk-upload/manufacturer': 'view_bulk_upload_scr',
  '/bulk-upload/model': 'view_bulk_upload_scr',
  '/bulk-upload/asset_status': 'view_bulk_upload_scr',
  '/bulk-upload/asset_condition': 'view_bulk_upload_scr',
};

// Route-based permission mapping
export const ROUTE_PERMISSIONS: Record<string, string> = {
  '/dashboard': 'view_dashboard_scr',
  '/assets': 'view_asset_scr',
  '/assets/create': 'add_asset_scr',
  '/asset-transfer': 'view_asset_transfer_scr',
  '/asset-disposal': 'view_asset_disposal_scr',
  '/floor-plans': 'manage_floor_plan_scr',
  '/floor-plans/interactive-editor': 'manage_floor_plan_scr',
  '/floor-plans/interactive-editor/:id': 'view_floor_plan_scr',
  '/depreciation': 'view_depreciation_scr',
  '/reports': 'view_report_scr',
  '/audit/requests': 'view_audit_scr',
  '/audit/pending': 'view_audit_task_scr',
  '/audit/reverifier': 'view_audit_task_scr',
  '/audit/rfid-scanner': 'execute_audit_task_scr',
  '/rfid/readers': 'view_rfid_scr',
  '/rfid/reader-types': 'view_rfid_scr',
  '/rfid/asset-scans': 'view_rfid_scr',
  '/rfid/vehicles': 'view_rfid_scr',
  '/schedule-management/schedules': 'view_schedule_scr',
  '/schedule-management/activities': 'view_activity_scr',
  '/user-groups': 'view_user_group_scr',
  '/activity-types': 'view_activity_type_scr',
  '/checklist-templates': 'view_checklist_template_scr',
  '/cost-templates': 'view_cost_template_scr',
  '/asset-categories': 'view_asset_category_scr',
  '/asset-statuses': 'view_asset_status_scr',
  '/asset-conditions': 'view_asset_condition_scr',
  '/sites': 'view_site_scr',
  '/locations': 'view_location_scr',
  '/departments': 'view_department_scr',
  '/suppliers': 'view_supplier_scr',
  '/manufacturers': 'view_manufacturer_scr',
  '/models': 'view_model_scr',
  '/workflow-templates': 'view_workflow_scr',
  '/approval-tasks': 'view_workflow_scr',
  '/bulk-upload': 'view_bulk_upload_scr',
  '/users': 'view_user_scr',
  '/users/invite': 'add_user_scr',
  '/role-management': 'view_role_scr',
  '/profile': 'view_user_scr',
  '/custom-forms/entity-forms': 'view_custom_form_scr',
  '/custom-forms/list-masters': 'view_custom_form_scr',
};

/**
 * Checks if user has permission for a specific menu item
 */
export function hasMenuPermission(menuTitle: string, allowedActions: string[], parentPath?: string): boolean {
  // First check path-based permission for duplicate titles
  if (parentPath && PATH_PERMISSIONS[parentPath]) {
    return allowedActions.includes(PATH_PERMISSIONS[parentPath]);
  }
  
  const requiredPermission = MENU_PERMISSIONS[menuTitle];
  
  if (!requiredPermission) {
    return true;
  }
  
  return allowedActions.includes(requiredPermission);
}

/**
 * Filters menu items based on user's allowed UI actions
 */
export function getPermissionBasedMenuItems(
  menuItems: NavigationItem[],
  allowedActions: string[]
): NavigationItem[] {
  return menuItems
    .filter((item) => hasMenuPermission(item.title, allowedActions, item.path))
    .map((item) => {
      if (item.children?.length) {
        const filteredChildren = item.children.filter((child) =>
          hasMenuPermission(child.title, allowedActions, child.path)
        );
        
        // Only include parent if it has accessible children or if parent itself is accessible
        if (filteredChildren.length > 0) {
          return { ...item, children: filteredChildren };
        } else if (hasMenuPermission(item.title, allowedActions, item.path)) {
          // Parent is accessible but has no accessible children
          return { ...item, children: [] };
        }
      }
      return { ...item };
    })
    .filter(Boolean); // Remove any undefined items
}

/**
 * Checks if user has specific action permission for a module
 */
export function hasActionPermission(
  module: string,
  action: 'add' | 'edit' | 'delete' | 'execute' | 'view' | 'manage',
  allowedActions: string[]
): boolean {
  const permission = `${action}_${module}_scr`;
  return allowedActions.includes(permission);
}

/**
 * Checks if user has a custom permission
 */
export function hasCustomPermission(
  permission: string,
  allowedActions: string[]
): boolean {
  return allowedActions.includes(permission);
}

/**
 * Checks if user has view permission for a module
 */
export function hasViewPermission(module: string, allowedActions: string[]): boolean {
  return hasActionPermission(module, 'view', allowedActions);
}

/**
 * Gets the required permission for a route path
 */
export function getRoutePermission(path: string): string | null {
  // Check exact match first
  if (ROUTE_PERMISSIONS[path]) {
    return ROUTE_PERMISSIONS[path];
  }
  
  // Check if path starts with any registered route
  for (const [route, permission] of Object.entries(ROUTE_PERMISSIONS)) {
    if (path.startsWith(route + '/')) {
      return permission;
    }
  }
  
  return null;
}

/**
 * Checks if user has permission to access a route
 */
export function hasRoutePermission(path: string, allowedActions: string[]): boolean {
  const requiredPermission = getRoutePermission(path);
  
  if (!requiredPermission) {
    return true; // Allow access if no permission is defined
  }
  
  return allowedActions.includes(requiredPermission);
}